import logger from '@tcp/core/src/utils/loggerInstance';
import { getBrand, isClient } from '../../../../../utils/utils';
import { toSentenceCase } from '../../../../common/organisms/ProductPickup/utils/ProductPickup.utils';

export function withErrorHandling(callback, args) {
  try {
    return callback(args);
  } catch (error) {
    logger.error(`Error occurred in ${callback.name}:: `, error.message);
    return '';
  }
}

/**
 * Extracts the time portion from a date string in 'YYYY-MM-DD HH:mm:ss' format.
 * Returns the time in 'HH:mm' format.
 *
 * @param {string} dateString - The date string in 'YYYY-MM-DD HH:mm:ss' format
 * @returns {string} The time portion in 'HH:mm' format
 * @throws {Error} If the input format is invalid
 */
export function extractTimeFromDate(dateString) {
  if (typeof dateString !== 'string') {
    throw new Error('Input must be a string');
  }

  // Split the string into date and time parts
  const [datePart, timePart] = dateString.split(' ');

  if (!datePart || !timePart) {
    throw new Error('Invalid date format. Expected "YYYY-MM-DD HH:mm:ss"');
  }

  // Extract hours and minutes
  const [hours, minutes] = timePart.split(':');

  if (!hours || !minutes) {
    throw new Error('Invalid time format. Expected "HH:mm:ss"');
  }

  return `${hours}:${minutes}`;
}

/**
 * @function getStaticValues to get the static values of local schema.
 * @param {string} name - name of the store.
 * @returns {object}
 */
export function getStaticValues({ name }) {
  let url = '';
  if (isClient()) {
    url = window?.location?.href;
  }
  const brand = getBrand();
  const logo =
    brand === 'tcp'
      ? 'https://assets.theplace.com/image/upload/ecom/assets/static/icon/tcp-logo.svg'
      : 'https://assets.theplace.com/image/upload/ecom/assets/static/icon/gymboree-logo.svg';
  const image =
    'https://assets.theplace.com/image/upload/v1/ecom/assets/static/category/store-logo.webp';
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    telephone: '(215) 657-8220',
    priceRange: '$$',
    name,
    url,
    logo,
    image,
  };
}

/**
 * @description to get the store address details for local schema.
 * @function getAddressDetail
 * @param {object} store object which holds the store details like address, zipcode, country etc.
 * @returns {object}
 */
export function getAddressDetail(store) {
  return {
    '@type': 'PostalAddress',
    streetAddress: toSentenceCase(store?.basicInfo?.address?.addressLine1),
    addressLocality: toSentenceCase(store?.basicInfo?.address?.city),
    addressRegion: store?.basicInfo?.address?.state,
    postalCode: store?.basicInfo?.address?.zipCode,
    addressCountry: store?.basicInfo?.address?.country,
  };
}

/**
 * @description to get the store opening and closing hours for local schema.
 * @function getOpeningHoursSpecification
 * @param {Array} storeTimings array of store timings.
 * @returns {Array}
 */
export function getOpeningHoursSpecification(storeTimings) {
  if (Array.isArray(storeTimings) && storeTimings.length) {
    return storeTimings.map((storeTiming) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: toSentenceCase(storeTiming?.dayName),
      opens: withErrorHandling(extractTimeFromDate, storeTiming?.openIntervals[0]?.fromHour),
      closes: withErrorHandling(extractTimeFromDate, storeTiming?.openIntervals[0]?.toHour),
    }));
  }
  return null;
}

/**
 * @description generates local schema for store detail page for SEO.
 * @function storeDetailLocalSchemaGenerator
 * @param {object} store store object which contains basic info and address of store for local schema.
 * @param {Array} storeTimings array of store timings.
 * @returns {object} local schema for store detail page..
 */
export default function storeDetailLocalSchemaGenerator({ store, storeTimings }) {
  return {
    ...getStaticValues({ name: toSentenceCase(store?.basicInfo?.storeName) }),
    address: { ...getAddressDetail(store) },
    openingHoursSpecification: getOpeningHoursSpecification(storeTimings),
  };
}
